/**
 * Static English fallback translations.
 *
 * The Blocks platform's translation service lives at
 * /uilm/v1/Key/GetUilmFile and returns the i18n bundles for each module.
 * For a freshly-created project, that endpoint returns
 * `Not_Found: Application_Not_Found` until the application is registered
 * in the Blocks OS portal — which means the i18n loader fails and the UI
 * shows raw translation keys (e.g. "FIRST_NAME") instead of values.
 *
 * This file provides a minimal English fallback so the auth/activation
 * screens render sensibly during local dev. Update it as you add more
 * keys — the platform's translation service will still override these
 * when it's available.
 *
 * Why not put this in i18n.ts? Keeping it separate means the boilerplate
 * upstream merge doesn't conflict with our local addition.
 */

export const fallbackTranslations = {
  en: {
    common: {
      CONFIRM: 'Confirm',
      CANCEL: 'Cancel',
      SAVE: 'Save',
      LOADING: 'Loading…',
      SOMETHING_WENT_WRONG: 'Something went wrong',
      FAILED_TO_FETCH: 'Failed to fetch',
      BACK: 'Back',
      NEXT: 'Next',
      SUBMIT: 'Submit',
    },
    auth: {
      // activation page
      SET_YOUR_PASSWORD: 'Set your password',
      SECURE_ACCOUNT_STRONG_PASSWORD:
        'Secure your account with a strong password.',
      FIRST_NAME: 'First name',
      LAST_NAME: 'Last name',
      PASSWORD: 'Password',
      CONFIRM_PASSWORD: 'Confirm password',
      ENTER_YOUR_PASSWORD: 'Enter your password',
      ENTER_YOUR_EMAIL: 'Enter your email',
      CONFIRM_YOUR_PASSWORD: 'Confirm your password',
      PASSWORD_REQUIREMENTS: 'Password requirements',
      PASSWORD_MUST_MEET_THESE_REQUIREMENTS: 'Your password must meet these requirements:',
      BETWEEN_EIGHT_THIRTY_CHARACTERS: 'Between 8 and 30 characters',
      AT_LEAST_ONE_UPPERCASE_AND_ONE_LOWERCASE_LETTER:
        'At least one uppercase and one lowercase letter',
      AT_LEAST_ONE_DIGIT: 'At least one digit',
      AT_LEAST_ONE_SPECIAL_CHARACTER: 'At least one special character (@ $ ! % * ? &)',
      PASSWORDS_MATCH: 'Passwords match',
      // login
      LOG_IN: 'Log in',
      DONT_HAVE_ACCOUNT: "Don't have an account?",
      SIGN_UP: 'Sign up',
      EMAIL: 'Email',
      FORGOT_PASSWORD: 'Forgot password?',
      // success
      ACTIVATION_SUCCESS: 'Your account has been activated',
      // errors
      MISSING_INVITATION_TOKEN:
        'Missing invitation token — open the link from your invite email.',
      PASSWORDS_DO_NOT_MATCH: 'Passwords do not match.',
    },
    hotwheels: {
      // Dashboard
      DASHBOARD: 'Dashboard',
      MY_COLLECTION: 'My Collection',
      STATS: 'Statistics',
      WISHLIST: 'Wishlist',
      ADD_CAR: 'Add Car',
      TOTAL_CARS: 'Total Cars',
      FAVORITES: 'Favorites',
      TREASURE_HUNTS: 'Treasure Hunts',
      SUPER_TREASURE_HUNTS: 'Super Treasure Hunts',
      TOTAL_VALUE: 'Estimated Value',
      RECENTLY_ADDED: 'Recently added',
      CARS_BY_YEAR: 'Cars by year',
      CARS_BY_BRAND: 'Cars by brand',
      CARS_BY_SERIES: 'Cars by series',
      // Car fields
      BRAND: 'Brand',
      SERIES: 'Series',
      MODEL_NUMBER: 'Model #',
      RELEASE_YEAR: 'Year',
      COLOR: 'Color',
      SCALE: 'Scale',
      CONDITION: 'Condition',
      CONDITION_CARDED: 'Carded',
      CONDITION_LOOSE: 'Loose',
      CONDITION_OPENED: 'Opened',
      CONDITION_MINT: 'Mint',
      PURCHASE_PRICE: 'Purchase price',
      ESTIMATED_VALUE: 'Estimated value',
      PURCHASE_DATE: 'Purchase date',
      STORAGE_LOCATION: 'Storage location',
      DESCRIPTION: 'Description',
      FAVORITE: 'Favorite',
      TREASURE_HUNT: 'Treasure Hunt',
      SUPER_TREASURE_HUNT: 'Super Treasure Hunt',
      LIMITED_EDITION: 'Limited Edition',
      PUBLIC_VISIBILITY: 'Show on public profile',
      MAIN_IMAGE: 'Main image',
      ADDITIONAL_IMAGES: 'Additional images',
      // Empty state
      EMPTY_TITLE: 'Your garage is empty',
      EMPTY_DESCRIPTION: 'Add your first Hot Wheel and start building your collection.',
      EMPTY_CTA: 'Add your first car',
      // Search & filter
      SEARCH_PLACEHOLDER: 'Search by name, model #, series, brand…',
      ALL_BRANDS: 'All brands',
      ALL_SERIES: 'All series',
      ALL_YEARS: 'All years',
      ALL_CONDITIONS: 'All conditions',
      SORT_BY: 'Sort by',
      VIEW_GRID: 'Grid',
      VIEW_LIST: 'List',
      // Toast messages
      CAR_ADDED: 'Car added',
      CAR_ADDED_DESC: 'Saved to your collection.',
      CAR_UPDATED: 'Car updated',
      CAR_UPDATED_DESC: 'Your changes have been saved.',
      CAR_DELETED: 'Car deleted',
      CAR_DELETED_DESC: 'Removed from your collection.',
      WISHLIST_ADDED: 'Added to wishlist',
      WISHLIST_UPDATED: 'Wishlist updated',
      WISHLIST_DELETED: 'Removed from wishlist',
      // Stats page
      AVG_CAR_VALUE: 'Average value',
      OLDEST_CAR: 'Oldest car',
      NEWEST_CAR: 'Newest car',
      MOST_EXPENSIVE: 'Most expensive',
      ADDED_THIS_MONTH: 'Added this month',
      // Wishlist page
      ADD_TO_WISHLIST: 'Add to wishlist',
      CAR_NAME: 'Car name',
      PRIORITY: 'Priority',
      PRIORITY_LOW: 'Low',
      PRIORITY_MEDIUM: 'Medium',
      PRIORITY_HIGH: 'High',
      NOTES: 'Notes',
      MARK_ACQUIRED: 'Mark acquired',
      ACQUIRED: 'Acquired',
      // Image upload
      DRAG_DROP_IMAGE: 'Drag & drop an image here',
      OR_CLICK_TO_BROWSE: 'or click to browse',
      IMAGE_REQUIREMENTS: 'JPG, PNG or WebP. Max 5MB.',
      UPLOADING: 'Uploading…',
      REPLACE_IMAGE: 'Replace image',
      REMOVE_IMAGE: 'Remove image',
      // Forms / dialogs
      EDIT_CAR: 'Edit car',
      DELETE_CAR: 'Delete car',
      DELETE_CONFIRM_TITLE: 'Delete this car?',
      DELETE_CONFIRM_DESCRIPTION: 'This will permanently remove {name} from your collection. This action cannot be undone.',
      // Public profile
      PUBLIC_COLLECTION: 'Public collection',
      USERNAME: 'Username',
      FAVORITE_CARS: 'Favorite cars',
      EMPTY_PUBLIC: 'This user has not made any cars public yet.',
    },
  },
};
